import PageContent from "components/Layout/PageContent";
import StateTabs from "components/StateTabs/StateTabs";
import { DashboardGrid } from "containers/Dashboard/DashboardGrid";
import { useMemo } from "react";
import {
    CONTAINER_DASHBOARD_DEFAULT_TAB,
    ContainerDashboardAssoc,
} from "./useContainerDashboards";

/**
 * Container-screen dashboard tab bar (container_dashboards feature, R2 restructure).
 *
 * When a container (WORKSPACE / PROJECT / BUNDLE) has one or more additional
 * dashboards configured via the `container_dashboard` join, this renders a tab
 * strip directly below the record header:
 *
 *  - a built-in **"Default dashboard"** tab — when active the container's usual
 *    Usage & activity panel and resource tabs are shown (those sibling layout
 *    partials are rendered by ResourcesSingle and gated on {@link isDefaultTab});
 *  - one tab per additional dashboard — when active **only** that dashboard's
 *    widgets render here (no Usage & activity panel, no resource tabs).
 *
 * When the container has no additional dashboards this renders nothing, leaving
 * the built-in default view untouched.
 *
 * Purely presentational: the association data and active-tab selection are owned
 * by {@link useContainerDashboards} in ResourcesSingle, because the selection
 * also gates the sibling partials.
 */
export const ContainerDashboardTabs = ({
    assocs,
    activeTab,
    setActiveTab,
    activeAssoc,
}: {
    assocs: ContainerDashboardAssoc[] | null;
    activeTab: string;
    setActiveTab: (uid: string) => void;
    activeAssoc: ContainerDashboardAssoc | undefined;
}) => {
    const tabs = useMemo(
        () => [
            { uid: CONTAINER_DASHBOARD_DEFAULT_TAB, label: "Default dashboard" },
            ...(assocs ?? []).map((assoc) => ({
                uid: assoc.dashboard_id_dashboard!.id,
                label: assoc.dashboard_id_dashboard!.name,
            })),
        ],
        [assocs]
    );

    // No additional dashboards configured -> no tab strip; the built-in default
    // view renders as usual.
    if (!assocs || assocs.length === 0) {
        return null;
    }

    const activeConfig =
        activeTab !== CONTAINER_DASHBOARD_DEFAULT_TAB
            ? activeAssoc?.dashboard_id_dashboard?.value
            : null;

    return (
        <PageContent>
            <StateTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
            {activeConfig && <DashboardGrid config={activeConfig} />}
        </PageContent>
    );
};

export default ContainerDashboardTabs;
